import { AxiosError } from 'axios';
import { Dayjs } from 'dayjs';
import { getUser } from 'lib/api';
import { getDollarValue, getUfValue } from 'lib/api/cmf';
import {
  createSGRWarrantyRequestPruffConnect,
  getBusinessSGRWarrantyRequests,
} from 'lib/api/sgr';
import { CONVERSION_RATES } from 'lib/consts';
import { calculateDays } from 'lib/functions/dates';
import { errorHandler } from 'lib/functions/errors';
import { formatMoney, validaRut } from 'lib/functions/utils';
import { formatAmountByCurrency } from 'lib/functions/utils';
import { updateConversionRatesInRedux } from 'lib/hooks/useConvertionRates';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';
import { store } from 'services/redux/store';

const PAGE_SIZE = 10;

interface NewQuotationState {
  type: string;
  quotedAmount: string;
  quotedCurrency: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  tenderId: string;
  principalRut: string;
  unitCode: string | null;
  conversionRate: number;
}

const parseAmountInput = (value: string) => value.replace(/[^0-9]/g, '');

export const useWarranties = () => {
  const dispatch = useDispatch();
  const domain = window.location.hostname;
  const secretId = domain.split('.')[0];
  const auth = useSelector((state: RootState) => state.auth);
  const userId = auth.user?.id;
  const [warranties, setWarranties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [businessIdentifier, setBusinessIdentifier] = useState<string | null>(
    null,
  );
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  // New quotation dialog state
  const [openNewQuotation, setOpenNewQuotation] = useState(false);
  const [newQuotation, setNewQuotation] = useState<NewQuotationState>({
    type: '',
    quotedAmount: '',
    quotedCurrency: 'CLP',
    startDate: null,
    endDate: null,
    tenderId: '',
    principalRut: '',
    conversionRate: 1,
    unitCode: null,
  });

  // Form validation and calculation state
  const [convertedAmount, setConvertedAmount] = useState<number>(0);
  const [daysDifference, setDaysDifference] = useState<number>(0);
  const [rutError, setRutError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [rawAmount, setRawAmount] = useState('');
  const [conversionRates, setConversionRates] = useState(CONVERSION_RATES);

  const generateQuery = ({
    page,
    pageSize,
    status,
  }: {
    page: number;
    pageSize: number;
    status?: string[];
  }) => {
    let query = '';
    if (status && status.length > 0) {
      query += `status=${status.join(',')}&`;
    }
    query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchBusinessIdentifier = useCallback(async () => {
    const profile = await getUser(dispatch, { id: userId });
    if (!profile) throw new Error('Profile not found');
    const businessIdentifier = profile.company.taxIdentifier;
    setBusinessIdentifier(businessIdentifier);
  }, [dispatch, userId]);

  // Fetch warranties
  const fetchWarranties = useCallback(
    async (
      {
        page,
        pageSize,
        status,
      }: {
        page: number;
        pageSize: number;
        status?: string[];
      } = { page: 1, pageSize: PAGE_SIZE },
    ) => {
      try {
        setIsLoading(true);
        if (!businessIdentifier) return;
        const query = generateQuery({ page, pageSize, status });
        const response = await getBusinessSGRWarrantyRequests(
          dispatch,
          businessIdentifier,
          query,
        );
        if (response) {
          setWarranties(response.data);
          setPagination(response.pagination);
        }
      } catch (error) {
        console.error(error);
        setError(error instanceof Error ? error.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, businessIdentifier],
  );

  // Fetch conversion rates
  const fetchConversionRates = async () => {
    try {
      const [dollarResponse, ufResponse] = await Promise.all([
        getDollarValue(dispatch),
        getUfValue(dispatch),
      ]);

      const usdRate = dollarResponse?.dolar || 950;
      const ufRate = ufResponse?.uf || 39000;

      setConversionRates({
        USD: usdRate,
        UF: ufRate,
        CLP: 1,
      });

      // Solo actualizar Redux si no tenemos tasas válidas para hoy
      const today = new Date().toISOString().split('T')[0];
      const state = store.getState();
      const currentRates = state.conversionRates;

      if (currentRates.date !== today) {
        updateConversionRatesInRedux(usdRate, ufRate);
      }
    } catch (error) {
      console.error('Error fetching conversion rates:', error);
      setConversionRates(CONVERSION_RATES);
    }
  };

  // Handle new quotation submission
  const createQuotation = async () => {
    try {
      setIsLoading(true);
      if (!businessIdentifier) throw new Error('Business identifier not found');
      const body = {
        type: newQuotation.type,
        quotedCurrency: newQuotation.quotedCurrency,
        quotedAmount: Number(newQuotation.quotedAmount),
        startDate: newQuotation.startDate,
        endDate: newQuotation.endDate,
        tenderId: newQuotation.tenderId,
        principalRut: newQuotation.principalRut,
        conversionRate: newQuotation.conversionRate,
        unitCode: newQuotation.unitCode,
        businessIdentifier,
      };
      const response = await createSGRWarrantyRequestPruffConnect(
        dispatch,
        secretId,
        body,
      );
      if (response) setOpenNewQuotation(false);
    } catch (error: unknown) {
      errorHandler(dispatch, error, 'Error al crear la garantía');
      setError(
        error instanceof AxiosError
          ? error?.response?.data?.message
          : 'Ocurrió un error al crear la garantía',
      );
    } finally {
      resetForm();
      setIsLoading(false);
      void fetchWarranties();
    }
  };

  // Form handlers
  const handleOpenNewQuotation = () => {
    setOpenNewQuotation(true);
  };

  const handleCloseNewQuotation = () => {
    setOpenNewQuotation(false);
  };

  const updateNewQuotation = (updates: Partial<NewQuotationState>) => {
    setNewQuotation((prev) => ({ ...prev, ...updates }));
  };

  const updateRawAmount = (value: string) => {
    const parsed = parseAmountInput(value);
    setRawAmount(parsed);
    setNewQuotation((prev) => ({
      ...prev,
      quotedAmount: parsed,
    }));
  };

  const resetForm = () => {
    setNewQuotation({
      type: '',
      quotedAmount: '',
      quotedCurrency: 'CLP',
      startDate: null,
      endDate: null,
      tenderId: '',
      principalRut: '',
      conversionRate: 1,
      unitCode: null,
    });
    setRawAmount('');
    setConvertedAmount(0);
    setDaysDifference(0);
    setRutError(null);
    setDateError(null);
  };

  // Validation
  const isFormValid = Boolean(
    newQuotation.type &&
      newQuotation.quotedAmount &&
      newQuotation.startDate &&
      newQuotation.endDate &&
      newQuotation.principalRut &&
      !rutError &&
      !dateError,
  );

  useEffect(() => {
    void fetchConversionRates();
  }, [dispatch]);

  useEffect(() => {
    if (rawAmount && newQuotation.quotedCurrency) {
      const amount = parseInt(rawAmount, 10) || 0;
      const rate =
        conversionRates[
          newQuotation.quotedCurrency as keyof typeof conversionRates
        ];
      setConvertedAmount(amount * rate);
      setNewQuotation((prev) => ({
        ...prev,
        conversionRate: rate,
      }));
    } else {
      setConvertedAmount(0);
    }
  }, [rawAmount, newQuotation.quotedCurrency, conversionRates]);

  useEffect(() => {
    if (newQuotation.startDate && newQuotation.endDate) {
      const start = newQuotation.startDate.toDate();
      const end = newQuotation.endDate.toDate();
      const days = calculateDays(start, end);
      setDaysDifference(days);
    } else {
      setDaysDifference(0);
    }
  }, [newQuotation.startDate, newQuotation.endDate]);

  useEffect(() => {
    if (newQuotation.startDate && newQuotation.endDate) {
      if (newQuotation.endDate.isBefore(newQuotation.startDate, 'day')) {
        setDateError(
          'La fecha de término debe ser posterior a la fecha de inicio',
        );
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
  }, [newQuotation.startDate, newQuotation.endDate]);

  // Validate RUT
  useEffect(() => {
    if (newQuotation.principalRut && !validaRut(newQuotation.principalRut)) {
      setRutError('RUT inválido');
    } else {
      setRutError(null);
    }
  }, [newQuotation.principalRut]);

  useEffect(() => {
    void fetchBusinessIdentifier();
  }, [fetchBusinessIdentifier]);

  return {
    // Warranties data
    warranties,
    isLoading,
    setIsLoading,
    error,
    fetchWarranties,

    // Dialog state
    openNewQuotation,
    handleOpenNewQuotation,
    handleCloseNewQuotation,

    // Form state
    newQuotation,
    updateNewQuotation,
    rawAmount,
    updateRawAmount,
    convertedAmount,
    daysDifference,
    rutError,
    dateError,
    isFormValid,

    // Formatted values
    formattedAmount: formatAmountByCurrency(
      rawAmount,
      newQuotation.quotedCurrency,
    ),
    formattedConvertedAmount: formatMoney(convertedAmount),
    formattedConversionRate: formatMoney(newQuotation.conversionRate),

    // Handlers
    createQuotation,
    resetForm,

    // Pagination state
    pagination,
    setPagination,
  };
};
